import { z } from 'zod'
import type { Bloc } from './schema.ts'

/** Le canevas est une chaîne explicite ; les conditions restent portées par chaque bloc. */
export const GrapheParcours = z
  .object({
    version: z.literal(1),
    entree: z.string().max(160).nullable(),
    positions: z.record(
      z.string().max(160),
      z
        .object({
          x: z.number().finite().min(-1000000).max(1000000),
          y: z.number().finite().min(-1000000).max(1000000),
        })
        .strict(),
    ),
    liens: z
      .array(
        z
          .object({ de: z.string().max(160), vers: z.string().max(160) })
          .strict(),
      )
      .max(3600),
  })
  .strict()
export type GrapheParcours = z.infer<typeof GrapheParcours>

export function grapheDepuisBlocs(blocs: readonly Bloc[]): GrapheParcours {
  return {
    version: 1,
    entree: blocs[0]?.cle ?? null,
    positions: Object.fromEntries(
      blocs.map((b, i) => [
        b.cle,
        { x: 80 + (i % 4) * 320, y: 100 + Math.floor(i / 4) * 220 },
      ]),
    ),
    liens: blocs.slice(1).map((b, i) => ({ de: blocs[i]!.cle, vers: b.cle })),
  }
}

export function analyserGraphe(
  blocs: readonly Bloc[],
  graphe: GrapheParcours,
): { ordre: string[]; erreurs: string[]; complet: boolean } {
  const erreurs: string[] = [],
    ordre: string[] = []
  const cles = new Set(blocs.map((b) => b.cle))
  const titre = (cle: string) => blocs.find((b) => b.cle === cle)?.titre || cle
  const sortants = new Map<string, string[]>(),
    entrants = new Map<string, string[]>()
  if (!blocs.length) erreurs.push('Ajoutez une étape au canevas.')
  if (cles.size !== blocs.length)
    erreurs.push('Chaque étape du canevas doit avoir une référence différente.')
  if (!graphe.entree) erreurs.push('Choisissez l’étape de départ du parcours.')
  else if (!cles.has(graphe.entree))
    erreurs.push(`L’étape de départ « ${graphe.entree} » n’existe plus.`)
  for (const cle of Object.keys(graphe.positions))
    if (!cles.has(cle))
      erreurs.push(`La position « ${cle} » correspond à une étape inconnue.`)
  const vues = new Set<string>()
  for (const lien of graphe.liens) {
    if (!cles.has(lien.de) || !cles.has(lien.vers)) {
      erreurs.push(
        `La liaison « ${lien.de} → ${lien.vers} » cite une étape inconnue.`,
      )
      continue
    }
    const identifiant = JSON.stringify([lien.de, lien.vers])
    if (vues.has(identifiant)) {
      erreurs.push(
        `La liaison « ${titre(lien.de)} → ${titre(lien.vers)} » apparaît plusieurs fois.`,
      )
      continue
    }
    vues.add(identifiant)
    sortants.set(lien.de, [...(sortants.get(lien.de) ?? []), lien.vers])
    entrants.set(lien.vers, [...(entrants.get(lien.vers) ?? []), lien.de])
  }
  for (const [cle, suivants] of sortants)
    if (suivants.length > 1)
      erreurs.push(
        `« ${titre(cle)} » a plusieurs sorties. Reliez cette étape à une seule étape suivante ; utilisez une condition pour la rendre facultative.`,
      )
  for (const [cle, precedents] of entrants)
    if (precedents.length > 1)
      erreurs.push(
        `« ${titre(cle)} » a plusieurs entrées. Chaque étape suit une seule autre étape.`,
      )
  if (graphe.entree && (entrants.get(graphe.entree)?.length ?? 0) > 0)
    erreurs.push('L’étape de départ ne doit pas recevoir de liaison.')
  // Détecter aussi une boucle dans un groupe d’étapes déconnecté du départ.
  const examinees = new Set<string>(),
    actives = new Set<string>()
  const parcourir = (cle: string): boolean => {
    if (actives.has(cle)) return true
    if (examinees.has(cle)) return false
    actives.add(cle)
    for (const suivante of sortants.get(cle) ?? [])
      if (parcourir(suivante)) return true
    actives.delete(cle)
    examinees.add(cle)
    return false
  }
  for (const cle of cles)
    if (parcourir(cle)) {
      erreurs.push(
        'Une liaison crée une boucle. Le parcours doit avancer jusqu’à sa confirmation sans revenir à une étape précédente.',
      )
      break
    }
  let courant = graphe.entree
  const atteintes = new Set<string>()
  while (courant && cles.has(courant) && !atteintes.has(courant)) {
    ordre.push(courant)
    atteintes.add(courant)
    const suivantes = sortants.get(courant) ?? []
    courant = suivantes.length === 1 ? suivantes[0]! : null
  }
  const isolees = blocs.filter((b) => !atteintes.has(b.cle))
  if (isolees.length)
    erreurs.push(
      `Reliez toutes les étapes au départ : ${isolees.map((b) => `« ${titre(b.cle)} »`).join(', ')} ${isolees.length > 1 ? 'ne sont pas reliées' : 'n’est pas reliée'}.`,
    )
  return {
    ordre,
    erreurs: [...new Set(erreurs)],
    complet:
      erreurs.length === 0 && ordre.length === blocs.length && blocs.length > 0,
  }
}
